import React, { useState, useRef, useEffect } from 'react';
import { SketchPicker } from 'react-color';

interface IColorPickerProps {
  label?: string;
  color?: string;
  setColor: (color: string) => void;
}

const SketchPickerComponent = SketchPicker as any;

// Modern preset colors - based on latest trends and Pantone standards
const PRESET_COLORS = [
  // Row 1: Grayscale & Neutrals
  '#000000',
  '#1A1A1A',
  '#333333',
  '#4D4D4D',
  '#666666',
  '#808080',
  '#999999',
  '#B3B3B3',
  '#CCCCCC',
  '#FFFFFF',
  // Row 2: Pantone & Trendy Blues (including classic blues)
  '#0F4C75',
  '#2563EB',
  '#3B82F6',
  '#60A5FA',
  '#93C5FD',
  '#1E40AF',
  '#1E3A8A',
  '#1E293B',
  '#0EA5E9',
  '#06B6D4',
  // Row 3: Pantone & Trendy Greens (sage, emerald, mint)
  '#059669',
  '#10B981',
  '#34D399',
  '#6EE7B7',
  '#A7F3D0',
  '#065F46',
  '#047857',
  '#064E3B',
  '#86EFAC',
  '#D1FAE5',
  // Row 4: Pantone & Trendy Purples/Violets (including periwinkle)
  '#7C3AED',
  '#8B5CF6',
  '#A78BFA',
  '#C4B5FD',
  '#DDD6FE',
  '#5B21B6',
  '#4C1D95',
  '#6D28D9',
  '#9333EA',
  '#EC4899',
  // Row 5: Pantone Peach Fuzz & Warm Tones (2024 Color of the Year + coral, apricot)
  '#FFB3A7',
  '#FFC9B8',
  '#FFD4C4',
  '#F4A261',
  '#E76F51',
  '#D62828',
  '#F77F00',
  '#FCBF49',
  '#FFE66D',
  '#FEF3C7',
  // Row 6: Earthy & Terracotta Tones (warm browns, rust, amber)
  '#92400E',
  '#B45309',
  '#D97706',
  '#F59E0B',
  '#FBBF24',
  '#78350F',
  '#A16207',
  '#CA8A04',
  '#EAB308',
  '#FCD34D',
  // Row 7: Dusty Rose & Soft Pinks (mauve, blush, rose quartz)
  '#F87171',
  '#FCA5A5',
  '#FECACA',
  '#FEE2E2',
  '#FEF2F2',
  '#EF4444',
  '#DC2626',
  '#B91C1C',
  '#991B1B',
  '#7F1D1D',
  // Row 8: Modern Teals & Cyans (turquoise, aqua, seafoam)
  '#14B8A6',
  '#2DD4BF',
  '#5EEAD4',
  '#99F6E4',
  '#CCFBF1',
  '#0D9488',
  '#0F766E',
  '#115E59',
  '#134E4A',
  '#042F2E',
];

const ColorPicker = ({ label, color, setColor }: IColorPickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'preset' | 'custom'>('preset');
  const pickerRef = useRef<HTMLDivElement>(null);

  // Close picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      // Also close on Escape key
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false);
        }
      };
      document.addEventListener('keydown', handleEscape);

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen]);

  return (
    <div className="relative inline-block w-full" ref={pickerRef}>
      {label && (
        <label className="block mb-1 text-sm text-[#202223]">{label}</label>
      )}
      <div className="flex items-center gap-2">
        <div
          className="w-10 h-10 rounded-md cursor-pointer border border-[#c9cccf] hover:border-[#a6acb3] transition-colors shadow-sm relative"
          style={{ backgroundColor: color || '#ffffff' }}
          onClick={() => setIsOpen(!isOpen)}
        >
          {!color && (
            <div className="absolute inset-0 rounded-md bg-[repeating-linear-gradient(45deg,#e1e3e5_25%,transparent_25%,transparent_75%,#e1e3e5_75%,#e1e3e5),repeating-linear-gradient(45deg,#e1e3e5_25%,transparent_25%,transparent_75%,#e1e3e5_75%,#e1e3e5)] bg-[length:8px_8px] bg-[0_0,4px_4px]" />
          )}
        </div>
        {color && (
          <span className="text-sm text-[#6d7175] font-mono">
            {color.toUpperCase()}
          </span>
        )}
      </div>
      {isOpen && (
        <>
          {/* Backdrop overlay */}
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            style={{ backgroundColor: 'rgba(0, 0, 0, 0.1)' }}
          />
          {/* Color Picker Popup */}
          <div className="absolute left-0 mt-2 z-50 shadow-xl bg-white border border-[#e1e3e5] rounded-lg overflow-hidden w-[280px] mr-6">
            {/* Toggle Tabs */}
            <div className="flex border-b border-[#e1e3e5]">
              <button
                type="button"
                onClick={() => setViewMode('preset')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                  viewMode === 'preset'
                    ? 'text-[#2563EB] border-b-2 border-[#2563EB] bg-[#f6f6f7]'
                    : 'text-[#6d7175] hover:text-[#202223] hover:bg-[#fafbfb]'
                }`}
              >
                Preset
              </button>
              <button
                type="button"
                onClick={() => setViewMode('custom')}
                className={`flex-1 px-4 py-2.5 text-sm font-medium transition-colors ${
                  viewMode === 'custom'
                    ? 'text-[#2563EB] border-b-2 border-[#2563EB] bg-[#f6f6f7]'
                    : 'text-[#6d7175] hover:text-[#202223] hover:bg-[#fafbfb]'
                }`}
              >
                Custom
              </button>
            </div>

            {viewMode === 'preset' ? (
              /* Preset Color Swatches */
              <div className="px-4 py-4 pr-6">
                <p className="text-xs font-medium text-[#202223] mb-3">
                  Choose a preset color
                </p>
                <div className="grid grid-cols-10 gap-1.5">
                  {PRESET_COLORS.map((presetColor) => (
                    <button
                      key={presetColor}
                      type="button"
                      className={`w-5 h-5 rounded border-2 transition-all hover:scale-110 ${
                        color?.toUpperCase() === presetColor.toUpperCase()
                          ? 'border-[#2563EB] ring-2 ring-[#2563EB] ring-opacity-30'
                          : 'border-[#e1e3e5] hover:border-[#c9cccf]'
                      }`}
                      style={{ backgroundColor: presetColor }}
                      onClick={() => {
                        setColor(presetColor);
                        setIsOpen(false);
                      }}
                      title={presetColor}
                    />
                  ))}
                </div>
              </div>
            ) : (
              /* Custom Color Picker */
              <div className="px-4 py-4 pr-6">
                <p className="text-xs font-medium text-[#202223] mb-3">
                  Choose a custom color
                </p>
                <div className="pr-2">
                  <SketchPickerComponent
                    color={color || '#000000'}
                    onChangeComplete={(newColor: { hex: string }) =>
                      setColor(newColor.hex)
                    }
                    disableAlpha={false}
                    width="248px"
                  />
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ColorPicker;
